import {
  AioExternalReplacements,
  AioExternalSingle,
  AioReplacement,
} from "../../additional/interface";
import { updateExternals } from "../../additional/updateExternals";
import { AitRowData } from "./AitRowData";
import { TlfViewModeConfig } from "./interface";
import { removeRowRepeatInfo } from "./removeRowRepeatInfo";
import { replaceRows } from "./replaceRows";
import { singleReplacements } from "./singleReplacements";

/**
 * Repeat rows based on repeat number array with potential for partial repeats.
 *
 * @param rows - Rows to process
 * @param defaultCellWidth - Default cell width for new cells
 * @param getTextFromT - Function to get text from generic type T
 * @param replaceTextInT - Function to replace text in generic type T
 * @param blankT - Blank value of generic type T
 * @param replacements - Optional replacements to process
 * @param spaceAfter - Optional flag to add space after the last row
 * @param tlfViewMode - Optional TLF view mode configuration
 * @param externalLists - Optional external lists for replacements
 * @param externalSingles - Optional external singles for single replacements
 * @returns AitRowData<T>[] - The processed rows with repeats applied
 */
export const repeatRows = <T>(
  rows: AitRowData<T>[],
  defaultCellWidth: number,
  getTextFromT: (s: T) => string[],
  replaceTextInT: (s: T, oldPhrase: string, newPhrase: T) => T,
  blankT: T,
  replacements?: AioReplacement<T>[],
  spaceAfter?: boolean,
  tlfViewMode?: TlfViewModeConfig,
  externalLists?: AioExternalReplacements<T>[],
  externalSingles?: AioExternalSingle<T>[],
): AitRowData<T>[] => {
  // Create initial return
  let newRows: AitRowData<T>[] = removeRowRepeatInfo<T>(rows);

  // Strip repeat data if flagged
  if (tlfViewMode?.noRepeatProcessing) {
    newRows[newRows.length - 1].spaceAfter = spaceAfter;
    return newRows;
  }

  // Process rows if there are replacements
  const extReplacements = updateExternals(replacements, externalLists);

  // Check other spaceAfter if there are replacements
  if (extReplacements && extReplacements.length > 0) {
    for (let si = 0; si < extReplacements.length; si++) {
      // Data fix
      // istanbul ignore next
      if (si > 0 && extReplacements[si].includeTrailing) {
        extReplacements[si].includeTrailing = false;
      }
      // Run current replacement
      newRows = replaceRows(
        newRows,
        defaultCellWidth,
        blankT,
        getTextFromT,
        replaceTextInT,
        extReplacements[si],
      );
    }
  }

  // Add spaceAfter for the group
  /* istanbul ignore else */
  if (spaceAfter) {
    newRows[newRows.length - 1].spaceAfter = true;
  }
  // Check row space after
  newRows.forEach((row) => {
    if (row.cells.some((cell) => cell.spaceAfterRepeat)) {
      row.spaceAfter = true;
    }
  });

  // Single post processing replacements
  newRows = singleReplacements(externalSingles, newRows, replaceTextInT);

  return newRows;
};
